import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBuilding,
  FaHotel,
  FaTree,
  FaDove,
  FaSpa,
  FaGem,
  FaMountain,
  FaMugHot,
  FaRoad,
  FaWindowMaximize,
} from 'react-icons/fa';

const ConcernsMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  const iconMap = {
    building: FaBuilding,
    hotel: FaHotel,
    tree: FaTree,
    dove: FaDove,
    spa: FaSpa,
    gem: FaGem,
    mountain: FaMountain,
    mug: FaMugHot,
    road: FaRoad,
    window: FaWindowMaximize,
  };

  const concerns = [
    { name: "Chuti Group", link: "https://www.facebook.com/chutigroup.org/", icon: "building" },
    { name: "Chuti Resort Gazipur", link: "https://chutiresort.com/", icon: "hotel" },
    { name: "Chuti Resort Purbachal", link: "https://chutiresortpurbachal.com/", icon: "hotel" },
    { name: "Chuti Aronnobash Resort", link: "https://chutiaronnobash.com/", icon: "tree" },
    { name: "Chuti Forest Eagle Resort", link: "https://www.facebook.com/chutiforesteagleresort/", icon: "dove" },
    { name: "Chuti Harmony", link: "https://www.chutiharmony.com/", icon: "spa" },
    { name: "Chuti Signature", link: "https://www.facebook.com/chutisignature/", icon: "gem" },
    { name: "Chuti Valley", link: "https://www.facebook.com/chutivalley/", icon: "mountain" },
    { name: "Saltanat Tea Resort", link: "https://www.facebook.com/saltanattearesort/", icon: "mug" },
    { name: "Saltanat Highway Village", link: "https://www.facebook.com/SaltanatHighwayVillage.bd/", icon: "road" },
    { name: "Chutir Janala", link: "https://www.facebook.com/chutirjanala.org/?_rdr", icon: "window" },
  ];

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const openMenu = () => setIsOpen(true);
    const toggleMenu = () => setIsOpen((prev) => !prev);
    const closeMenu = () => setIsOpen(false);

    window.addEventListener('openConcernsMenu', openMenu);
    window.addEventListener('toggleConcernsMenu', toggleMenu);
    window.addEventListener('closeConcernsMenu', closeMenu);

    return () => {
      window.removeEventListener('openConcernsMenu', openMenu);
      window.removeEventListener('toggleConcernsMenu', toggleMenu);
      window.removeEventListener('closeConcernsMenu', closeMenu);
    };
  }, []);

  const handleClose = () => setIsOpen(false);

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 bottom-0 top-[44px] md:top-[56px] bg-black/50 backdrop-blur-sm z-[60]"
            onClick={handleClose}
          />
        )}
      </AnimatePresence>

      {/* Slide-in Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-[44px] md:top-[56px] right-0 h-[calc(100vh-44px)] md:h-[calc(100vh-56px)] w-full md:w-96 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-600 shadow-2xl z-[70] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-900 to-blue-800 p-6 border-b border-white/20 backdrop-blur-sm z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white font-fancy mb-1">Our Concerns</h2>
                  <p className="text-sm text-white/80 font-body">Explore Chuti Group Properties</p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white hover:text-yellow-400 transition-colors duration-300 p-2 hover:bg-white/10 rounded-full"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Concerns List */}
            <div className="p-6 space-y-3">
              {concerns.map((concern, index) => (
                <motion.a
                  key={concern.name}
                  href={concern.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  whileHover={{ scale: 1.03, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  className="block bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-xl p-4 border border-white/20 hover:border-yellow-400 transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                      {React.createElement(iconMap[concern.icon] || FaBuilding, { className: "text-blue-900 text-lg" })}
                    </div>
                    
                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold font-fancy text-base mb-0.5 group-hover:text-yellow-400 transition-colors duration-300">
                        {concern.name}
                      </h3>
                      <p className="text-white/60 text-xs font-body truncate">
                        Click to visit
                      </p>
                    </div>

                    {/* Arrow Icon */}
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-yellow-400 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/20 mt-4">
              <p className="text-white/60 text-xs text-center font-body">
                Part of the Chuti group Family
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConcernsMenu;
